import {Concept} from 'types';
import type {
  AnyValidation,
  Condition,
  Part,
  TypedSchema,
  TypedSchemaModifierMap,
} from 'schemaDefinition/types';
import {Separator} from 'schemaDefinition/types';
import type {
  ExpressionImportProps,
  ExpressionV4,
} from 'services/metadata/types';
import type {MetadataModifierKey} from '../types';
import {StatusModifierKey, WorkBook, WorkMerchandise} from '../types';
import {CodeListRef} from '../references';
import {makeHasAgentsRoleRegex} from './functions';
import {
  validateExternalLinkedAgentId,
  validateLinkedAgentId,
} from './partAgentsValidations';

type ExpressionSchema = TypedSchema<ExpressionV4, ExpressionImportProps>;

type ExpressionCondition<
  TName extends keyof ExpressionV4 = keyof ExpressionV4,
> = Condition<
  ExpressionV4,
  ExpressionV4,
  ExpressionV4[TName],
  ExpressionImportProps
>;

type ExpressionValidation<TName extends keyof ExpressionV4> = AnyValidation<
  ExpressionV4,
  ExpressionV4,
  ExpressionV4[TName],
  ExpressionImportProps
>;

type ExpressionPart = Part<ExpressionV4, ExpressionV4, ExpressionImportProps>;

/**
 * Expression schema
 */

const expressionContentPart: ExpressionPart = {
  type: 'codelist',
  name: 'content',
  cardinality: 'multiple',
  codelistId: 'EXPRESSION_CONTENT',
  key: 'content',
};

const expressionValidation_EbookOrPhysicalAndChildrenRequiresOmslag: ExpressionValidation<'agents'> =
  {
    // Ebook/physical AND !adult AND !schoolbooks => 'Omslage' role must be set, i.e.: (!(Ebook/physical AND children) or 'Omslag') = (Audio OR !children or 'Omslag')
    messageKey: 'EbookOrPhysicalAndChildrenRequiresOmslagRole',
    level: 'warning',
    $or: [
      // audio (= not ebok or physcial)
      {
        $eq: [{$ref: '/expressionFormat'}, CodeListRef.EXPRESSION_FORMAT.AUDIO],
      },
      // not schoolbooks
      {
        $not: {
          $includes: {
            arg1: {$ref: '^work.literatureType'},
            arg2: [
              CodeListRef.LITERATURE_TYPE.Faglitteratur,
              CodeListRef.LITERATURE_TYPE['Lærebøker (høyere utd.)'],
              CodeListRef.LITERATURE_TYPE.Skjønnlitteratur,
            ],
          },
          default: true,
        },
      },
      // not children
      {
        $not: {
          $includes: {
            arg1: {$ref: '^work.intellectualLevel'},
            arg2: [
              CodeListRef.INTELLECTUAL_LEVEL['0-3'],
              CodeListRef.INTELLECTUAL_LEVEL['3-6'],
              CodeListRef.INTELLECTUAL_LEVEL['6-9'],
              CodeListRef.INTELLECTUAL_LEVEL['9-12'],
              CodeListRef.INTELLECTUAL_LEVEL['12-16'],
            ],
          },
          default: true,
        },
      },
      // has role 'Omslag'
      {
        // Will match against JSON of agents w/roles, use quotes to match against string in roles array
        regex: `"${CodeListRef.EXPRESSION_ROLE_TYPE['Omslag av']}"`,
        default: false,
      },
    ],
  };

const expressionValidation_AudioRequiresReader: ExpressionValidation<'agents'> =
  {
    // AUDIO => 'Lest av' role must be set, i.e.: (!AUDIO or 'Lest av)
    messageKey: 'AudioRequiresReaderRole',
    level: 'warning',
    op: 'or',
    arg: [
      {
        op: 'not',
        arg: {
          arg: {$ref: '/expressionFormat'},
          regex: CodeListRef.EXPRESSION_FORMAT.AUDIO,
          default: false,
        },
      },
      {
        // Will match against JSON of agents w/roles, use quotes to match against string in roles array
        regex: `"${CodeListRef.EXPRESSION_ROLE_TYPE['Lest av']}"`,
        default: false,
      },
    ],
  };

const expressionValidation_notTextWhenBilledkunstner: ExpressionValidation<'expressionFormat'> =
  {
    // Billedkunstner => !TEXT
    messageKey: 'expressionFormat.notText.when.billedkunstner',
    level: 'warning',
    $or: [
      {
        $not: {$eq: CodeListRef.EXPRESSION_FORMAT.TEXT},
      },
      {
        $not: {
          regex: makeHasAgentsRoleRegex(
            CodeListRef.WORK_ROLE_TYPE.Billedkunstner,
          ),
          arg: {$ref: '^work.agents'},
          default: false,
        },
      },
    ],
  };

const hasLanguage: ExpressionCondition = {arg: {$ref: '/languages[0]'}};
const hasWorkLanguage: ExpressionCondition = {
  arg: {$ref: '^work.languages[0]'},
};

const expressionValidation_DiffLanguageRequiresTranslator: ExpressionValidation<'agents'> =
  {
    // work languages != expression language => 'Oversatt av' role must be set, i.e.: (same language (or null) or 'Oversatt av')
    messageKey: 'DiffLanguageRequiresTranslatorRole',
    level: 'warning',
    op: 'or',
    arg: [
      {
        op: 'not',
        arg: hasLanguage,
      },
      {
        op: 'not',
        arg: hasWorkLanguage,
      },
      {
        op: 'eq',
        arg1: {$ref: '/languages[0]'},
        arg2: {$ref: '^work.languages[0]'},
      },
      {
        // Will match against JSON of agents w/roles, use quotes to match against string in roles array
        regex: `"${CodeListRef.EXPRESSION_ROLE_TYPE['Oversatt av']}"`,
        default: false,
      },
    ],
  };

/*
 * If translator is set then work language cannot be same as expression language.
 */
const expressionValidation_translatorRequiresDiffLanguage: ExpressionValidation<'languages'> =
  {
    messageKey: 'translator.expression.requires.different.work.language',
    level: 'warning',
    $or: [
      {
        $not: {
          $includes: {
            $ref: '^work.languages[0]',
          },
          default: false,
        },
      },
      {
        $not: {
          arg: {$ref: '/agents'},
          // Needs quotes, because it may match somewhere in agent.id (uuid with b06)
          regex: `"${CodeListRef.EXPRESSION_ROLE_TYPE['Oversatt av']}"`,
          default: true,
        },
      },
    ],
  };

const expressionValidation_LanguagesShouldNotBeJustFlerspråklig: ExpressionValidation<'languages'> =
  {
    level: 'warning',
    $not: {
      $eq: [
        {$ref: ':languages'},
        [`"${CodeListRef.LANGUAGE.Flerspråklig}"`] as string[],
      ],
    },
    messageKey: 'languages.not.only.multilingual',
    default: true,
  };

const expressionValidation_RequiresMultipleLanguagesWhenMultipleOnWork: ExpressionValidation<'languages'> =
  {
    level: 'warning',
    $or: [
      // Not multiple languages on work
      {
        $not: {$ge: [{$ref: '^work.languages.length'}, 2]},
      },
      // Multiple languages on expression
      {
        $ge: [{$ref: ':languages.length'}, 2],
      },
    ],
    messageKey:
      'multiple.work.languages.requires.multiple.expression.languages',
    default: true,
  };

export const expressionDataSchema: ExpressionSchema = {
  name: Concept.expression,
  parts: [
    {
      type: 'linkedAgent',
      name: 'agents',
      cardinality: 'multiple',
      roleCodelistId: 'EXPRESSION_ROLE_TYPE',
      entitySubtypes: [Concept.person, Concept.corporation, Concept.event],
      compare: 'subValues',
    },
    Separator('card', 'none'),
    {
      type: 'codelist',
      name: 'languages',
      cardinality: 'multiple',
      codelistId: 'LANGUAGE',
      key: 'language',
    },
    {
      type: 'codelist',
      name: 'intermediateOriginalLanguage',
      codelistId: 'LANGUAGE',
    },
    {
      type: 'codelist',
      name: 'expressionFormat',
      codelistId: 'EXPRESSION_FORMAT',
      required: true,
    },
    {
      type: 'expand',
      role: 'expressionContentAndSpecialChars',
      when: [
        {
          condition: {
            op: 'eq',
            arg1: {$ref: '/expressionFormat'},
            arg2: CodeListRef.EXPRESSION_FORMAT.TEXT,
          },
          parts: [[expressionContentPart]],
        },
        {
          condition: {
            op: 'eq',
            arg1: {$ref: '/expressionFormat'},
            arg2: CodeListRef.EXPRESSION_FORMAT.ILLUSTRATED_TEXT,
          },
          parts: [[expressionContentPart]],
        },
      ],
    },
  ],
};

export const expressionSchemaModifiers: TypedSchemaModifierMap<
  ExpressionV4,
  MetadataModifierKey
> = {
  [`${WorkMerchandise}.${StatusModifierKey.precat}`]: [
    {
      name: 'expressionFormat',
      validation: [expressionValidation_notTextWhenBilledkunstner],
    },
    {
      name: 'languages',
      required: true,
    },
  ],
  [`${WorkMerchandise}.${StatusModifierKey.cat}`]: [
    {
      name: 'agents',
      listValidation: [
        expressionValidation_DiffLanguageRequiresTranslator,
        expressionValidation_AudioRequiresReader,
      ],
      validation: [validateLinkedAgentId, validateExternalLinkedAgentId],
    },
    {
      name: 'languages',
      required: true,
    },
  ],
  [`${WorkBook}.${StatusModifierKey.precat}`]: [
    {
      name: 'expressionFormat',
      validation: [expressionValidation_notTextWhenBilledkunstner],
    },
    {
      name: 'languages',
      required: true,
      listValidation: [
        expressionValidation_LanguagesShouldNotBeJustFlerspråklig,
        expressionValidation_RequiresMultipleLanguagesWhenMultipleOnWork,
      ],
    },
  ],
  [`${WorkBook}.${StatusModifierKey.cat}`]: [
    {
      name: 'languages',
      required: true,
      listValidation: [
        expressionValidation_translatorRequiresDiffLanguage,
        expressionValidation_LanguagesShouldNotBeJustFlerspråklig,
        expressionValidation_RequiresMultipleLanguagesWhenMultipleOnWork,
      ],
    },
    {
      name: 'agents',
      listValidation: [
        expressionValidation_DiffLanguageRequiresTranslator,
        expressionValidation_AudioRequiresReader,
        expressionValidation_EbookOrPhysicalAndChildrenRequiresOmslag,
      ],
      validation: [validateLinkedAgentId, validateExternalLinkedAgentId],
    },
  ],
};
